import type { RenderFragment, RenderInfo } from './api';
import { mergeSegmentFragment } from './fragmentState.ts';

export type FragmentUpdate = { revision: number; fragment: RenderFragment };

// A background response is a snapshot from request time. Keep newer object updates
// while still accepting server metadata and invalidation for every other fragment.
export function mergeRenderFragmentUpdates(render: RenderInfo, updates: Iterable<FragmentUpdate>, requestRevision: number): RenderInfo {
  for (const update of updates) {
    if (update.revision > requestRevision) render = mergeSegmentFragment(render, update.fragment);
  }
  return render;
}

export type RenderScope = 'fragments' | 'delivery';
export type LoadedRender = { projectId: string; scope: RenderScope; historical: boolean; hasFragments: boolean };
export type RenderLoadState = { visible?: LoadedRender; error: string; refreshing: boolean };
export type RenderLoadAction =
  | { type: 'reset' }
  | { type: 'refresh' }
  | { type: 'loaded'; value: LoadedRender }
  | { type: 'failed'; error: string };

export const initialRenderLoadState: RenderLoadState = { error: '', refreshing: false };

export function matchesRenderScope(loaded: LoadedRender | undefined, projectId: string | undefined, scope: RenderScope, historical: boolean) {
  return Boolean(loaded && loaded.projectId === projectId && (scope === 'fragments'
    ? loaded.hasFragments
    : loaded.scope === 'delivery' && loaded.historical === historical));
}

// Refreshing data must never discard an already mounted workspace. Only changing
// the project or explicitly selecting another delivery clears the visible scope.
export function renderLoadReducer(state: RenderLoadState, action: RenderLoadAction): RenderLoadState {
  switch (action.type) {
    case 'reset': return initialRenderLoadState;
    case 'refresh': return { ...state, error: '', refreshing: true };
    case 'loaded': return { visible: action.value, error: '', refreshing: false };
    case 'failed': return { ...state, error: action.error, refreshing: false };
  }
}
